import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api, { authApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';
import type { Fair, FairManagerApplicationCreate } from '../types';

interface ApplyNowFlowProps {
  fair: Fair;
  onClose: () => void;
}

type Step = 'account-check' | 'login' | 'signup' | 'application' | 'confirmation';

interface ApplicationFormData {
  applicant_name: string;
  email: string;
  phone: string;
  relationship_explanation: string;
}

export const ApplyNowFlow: React.FC<ApplyNowFlowProps> = ({ fair, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);

  const [currentStep, setCurrentStep] = useState<Step>(user ? 'application' : 'account-check');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Signup form state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');

  // Application form state
  const [applicationData, setApplicationData] = useState<ApplicationFormData>({
    applicant_name: user?.name || '',
    email: user?.email || '',
    phone: '',
    relationship_explanation: ''
  });

  // Accessibility: Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && currentStep !== 'confirmation') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [currentStep, onClose]);

  // Accessibility: Focus trap and initial focus
  useEffect(() => {
    if (modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      // Set initial focus
      firstElement?.focus();

      // Focus trap handler
      const handleTabKey = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') return;

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      };

      document.addEventListener('keydown', handleTabKey);
      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [currentStep]);

  const handleAccountCheck = (hasAccountValue: boolean) => {
    setCurrentStep(hasAccountValue ? 'login' : 'signup');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await authApi.login(loginEmail, loginPassword);
      // After successful login, update application data
      setApplicationData({
        ...applicationData,
        applicant_name: loginEmail.split('@')[0], // Fallback to email username
        email: loginEmail
      });
      setCurrentStep('application');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
      // Clear password from memory immediately after use (PR #1476 Code Review)
      setLoginPassword('');
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (signupPassword !== signupConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (signupPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/register', {
        email: signupEmail,
        password: signupPassword,
        confirm_password: signupConfirmPassword,
        display_name: signupName || undefined
      });

      // Registration successful, user is automatically logged in
      const { access_token, refresh_token, user: registeredUser } = response.data;

      // Store authentication data
      localStorage.setItem('access_token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(registeredUser));

      // Update application data with signup info
      setApplicationData({
        ...applicationData,
        applicant_name: signupName || registeredUser.email.split('@')[0],
        email: signupEmail
      });
      setCurrentStep('application');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
      // Clear passwords from memory immediately after use (PR #1476 Code Review)
      setSignupPassword('');
      setSignupConfirmPassword('');
    }
  };

  const handleApplicationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (applicationData.relationship_explanation.length < 10) {
      setError('Please provide a more detailed explanation of your relationship to this fair (at least 10 characters)');
      return;
    }

    setLoading(true);

    try {
      const applicationPayload: FairManagerApplicationCreate = {
        fair_id: fair.id,
        user_id: user?.id,
        applicant_name: applicationData.applicant_name,
        email: applicationData.email,
        phone: applicationData.phone,
        relationship_explanation: applicationData.relationship_explanation
      };

      // Use the API wrapper instead of raw fetch for consistency
      // This ensures proper auth token handling, CSRF protection, and error handling
      await api.post('/api/fair-manager-applications', applicationPayload);

      setCurrentStep('confirmation');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const renderAccountCheck = () => (
    <div className="space-y-6">
      <div>
        <h2 id="modal-title" className="text-2xl font-bold text-gray-900 mb-2">Apply to Manage Fair</h2>
        <p className="text-gray-600">
          Apply to become the manager for "{fair.name}". You'll be able to manage booths, vendors, and fair details.
        </p>
      </div>

      <div className="bg-secondary-light/10 border border-primary-light rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Current Fair Contact</h3>
        <div className="text-sm text-primary-dark space-y-1">
          <p><strong>Name:</strong> {fair.seed_contact_name}</p>
          <p><strong>Email:</strong> {fair.seed_contact_email}</p>
          <p><strong>Phone:</strong> {fair.seed_contact_phone}</p>
        </div>
      </div>

      <div>
        <p className="text-gray-700 mb-4 font-medium">Do you already have an account?</p>
        <div className="flex gap-4">
          <button
            onClick={() => handleAccountCheck(true)}
            className="flex-1 py-3 px-6 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
          >
            Yes, I have an account
          </button>
          <button
            onClick={() => handleAccountCheck(false)}
            className="flex-1 py-3 px-6 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
          >
            No, create new account
          </button>
        </div>
      </div>

      <button
        onClick={onClose}
        className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors"
      >
        Cancel
      </button>
    </div>
  );

  const renderLogin = () => (
    <div className="space-y-6">
      <div>
        <h2 id="modal-title" className="text-2xl font-bold text-gray-900 mb-2">Sign In</h2>
        <p className="text-gray-600">Sign in to continue with your application.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            id="login-email"
            type="email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            id="login-password"
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="••••••••"
          />
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setCurrentStep('account-check')}
            className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2 px-4 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </div>
      </form>
    </div>
  );

  const renderSignup = () => (
    <div className="space-y-6">
      <div>
        <h2 id="modal-title" className="text-2xl font-bold text-gray-900 mb-2">Create Account</h2>
        <p className="text-gray-600">Create an account to submit your application.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSignup} className="space-y-4">
        <div>
          <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <input
            id="signup-name"
            type="text"
            value={signupName}
            onChange={(e) => setSignupName(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="John Doe"
          />
        </div>

        <div>
          <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            id="signup-email"
            type="email"
            value={signupEmail}
            onChange={(e) => setSignupEmail(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            id="signup-password"
            type="password"
            value={signupPassword}
            onChange={(e) => setSignupPassword(e.target.value)}
            required
            minLength={8}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="••••••••"
          />
          <p className="text-xs text-gray-500 mt-1">At least 8 characters</p>
        </div>

        <div>
          <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password
          </label>
          <input
            id="signup-confirm-password"
            type="password"
            value={signupConfirmPassword}
            onChange={(e) => setSignupConfirmPassword(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="••••••••"
          />
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setCurrentStep('account-check')}
            className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2 px-4 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </div>
      </form>
    </div>
  );

  const renderApplication = () => (
    <div className="space-y-6">
      <div>
        <h2 id="modal-title" className="text-2xl font-bold text-gray-900 mb-2">Application Form</h2>
        <p className="text-gray-600">
          Please provide information about yourself and your relationship to this fair.
        </p>
      </div>

      <div className="bg-secondary-light/10 border border-primary-light rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Current Fair Contact</h3>
        <div className="text-sm text-primary-dark space-y-1">
          <p><strong>Name:</strong> {fair.seed_contact_name}</p>
          <p><strong>Email:</strong> {fair.seed_contact_email}</p>
          <p><strong>Phone:</strong> {fair.seed_contact_phone}</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleApplicationSubmit} className="space-y-4">
        <div>
          <label htmlFor="applicant-name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <input
            id="applicant-name"
            type="text"
            value={applicationData.applicant_name}
            onChange={(e) => setApplicationData({ ...applicationData, applicant_name: e.target.value })}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="John Doe"
          />
        </div>

        <div>
          <label htmlFor="applicant-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            id="applicant-email"
            type="email"
            value={applicationData.email}
            onChange={(e) => setApplicationData({ ...applicationData, email: e.target.value })}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label htmlFor="applicant-phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
          </label>
          <input
            id="applicant-phone"
            type="tel"
            value={applicationData.phone}
            onChange={(e) => setApplicationData({ ...applicationData, phone: e.target.value })}
            required
            pattern="^\+?[\d\s\-\(\)]+$"
            title="Phone number must contain only digits, spaces, hyphens, parentheses, and optional + prefix"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="(555) 123-4567"
          />
        </div>

        <div>
          <label htmlFor="relationship-explanation" className="block text-sm font-medium text-gray-700 mb-1">
            Relationship to Fair
          </label>
          <textarea
            id="relationship-explanation"
            value={applicationData.relationship_explanation}
            onChange={(e) => setApplicationData({ ...applicationData, relationship_explanation: e.target.value })}
            required
            minLength={10}
            rows={5}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="Please explain your relationship to this fair and why you should manage it. For example: 'I am the original organizer of this fair...' or 'I have been involved with this fair for several years...'"
          />
          <p className="text-xs text-gray-500 mt-1">
            Minimum 10 characters. Be specific about your connection to this fair.
          </p>
        </div>

        <div className="flex gap-4">
          {!user && (
            <button
              type="button"
              onClick={() => setCurrentStep('account-check')}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Back
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className={`${user ? 'w-full' : 'flex-1'} py-2 px-4 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </div>
      </form>

      {user && (
        <button
          onClick={onClose}
          className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          Cancel
        </button>
      )}
    </div>
  );

  const renderConfirmation = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 id="modal-title" className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
        <p className="text-gray-600">
          Your application to manage "{fair.name}" has been submitted successfully.
        </p>
      </div>

      <div className="bg-secondary-light/10 border border-primary-light rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
        <ul className="text-sm text-primary-dark space-y-2 list-disc list-inside">
          <li>Our team will review your application</li>
          <li>You'll receive an email notification with the decision</li>
          <li>If approved, you'll gain full management access to the fair</li>
          <li>You can check your application status in your account dashboard</li>
        </ul>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-2">Application Details</h3>
        <div className="text-sm text-gray-700 space-y-1">
          <p><strong>Fair:</strong> {fair.name}</p>
          <p><strong>Your Name:</strong> {applicationData.applicant_name}</p>
          <p><strong>Your Email:</strong> {applicationData.email}</p>
          <p><strong>Your Phone:</strong> {applicationData.phone}</p>
        </div>
      </div>

      <button
        onClick={() => {
          onClose();
          navigate('/dashboard');
        }}
        className="w-full py-3 px-6 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
      >
        Go to Dashboard
      </button>

      <button
        onClick={onClose}
        className="w-full py-2 text-gray-600 hover:text-gray-800 transition-colors"
      >
        Close
      </button>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
      >
        {currentStep === 'account-check' && renderAccountCheck()}
        {currentStep === 'login' && renderLogin()}
        {currentStep === 'signup' && renderSignup()}
        {currentStep === 'application' && renderApplication()}
        {currentStep === 'confirmation' && renderConfirmation()}
      </div>
    </div>
  );
};

export default ApplyNowFlow;
